import { useState, useEffect, useRef } from 'react';
import { User, UseUserResult, PromoCode } from '../types';
import { UserService } from '../services/userService';
import { PromoService } from '../services/promoService';
import { ApiError } from '../errors/ApiError';
import { fetchWithErrorHandling } from '../services/apiUtils';

// Extend window to store global user state
declare global {
  interface Window {
    __USER_FETCH_IN_PROGRESS__?: boolean;
    __USER_FETCHED__?: boolean;
    __USER_DATA__?: User | null;
    __USER_PROMO_CODES__?: Record<string, PromoCode>;
  }
}

// Initialize global state on window (only in browser environment)
if (typeof window !== 'undefined') {
  if (window.__USER_FETCH_IN_PROGRESS__ === undefined) {
    window.__USER_FETCH_IN_PROGRESS__ = false;
  }
  if (window.__USER_FETCHED__ === undefined) {
    window.__USER_FETCHED__ = false;
  }
  if (window.__USER_DATA__ === undefined) {
    window.__USER_DATA__ = null;
  }
  if (window.__USER_PROMO_CODES__ === undefined) {
    window.__USER_PROMO_CODES__ = {};
  }
}

export function useUser(): UseUserResult {
  const [user, setUser] = useState<User | null>(typeof window !== 'undefined' ? window.__USER_DATA__ || null : null);
  // Initialize with the actual window object reference to avoid creating new objects
  const [promoCodes, setPromoCodes] = useState<Record<string, PromoCode>>(() => {
    if (typeof window !== 'undefined' && window.__USER_PROMO_CODES__) {
      return window.__USER_PROMO_CODES__;
    }
    return {};
  });
  const [loading, setLoading] = useState(typeof window !== 'undefined' ? !window.__USER_FETCHED__ : true);
  const [error, setError] = useState<string | null>(null);
  const fetchedRef = useRef(false);

  useEffect(() => {
    let isMounted = true;

    // Prevent duplicate fetches at component level (guard for React StrictMode double-invoke)
    /* c8 ignore next 3 */
    if (fetchedRef.current) {
      return;
    }
    fetchedRef.current = true;

    // If data already fetched globally, just update local state
    if (typeof window !== 'undefined' && window.__USER_FETCHED__) {
      if (isMounted) {
        if (window.__USER_DATA__) {
          setUser(window.__USER_DATA__);
        }
        if (window.__USER_PROMO_CODES__) {
          setPromoCodes(window.__USER_PROMO_CODES__);
        }
        setLoading(false);
      }
      return;
    }

    // If fetch is in progress, wait for it and then update
    if (typeof window !== 'undefined' && window.__USER_FETCH_IN_PROGRESS__) {
      const pollInterval = setInterval(() => {
        if (window.__USER_FETCHED__) {
          clearInterval(pollInterval);
          if (isMounted) {
            if (window.__USER_DATA__) {
              setUser(window.__USER_DATA__);
            }
            if (window.__USER_PROMO_CODES__) {
              setPromoCodes(window.__USER_PROMO_CODES__);
            }
            setLoading(false);
          }
        }
      }, 50); // Poll every 50ms

      return () => {
        clearInterval(pollInterval);
      };
    }

    if (typeof window !== 'undefined') {
      window.__USER_FETCH_IN_PROGRESS__ = true;
    }

    const fetchUserData = async () => {
      let authenticatedUser: User | null = null;

      try {
        if (isMounted) {
          setLoading(true);
          setError(null);
        }

        // Fetch user data
        try {
          const userData = await UserService.getCurrentUser();
          // Treat null, empty object, or undefined as guest (not authenticated)
          authenticatedUser = (userData && Object.keys(userData).length > 0) ? userData : null;
          if (typeof window !== 'undefined') {
            window.__USER_DATA__ = authenticatedUser;
          }
          if (isMounted) {
            setUser(authenticatedUser);
          }
        } catch (userError: unknown) {
          // Handle 401/403 gracefully - user is not authenticated (guest mode)
          const statusCode = userError instanceof ApiError ? userError.statusCode : null;
          if (statusCode === 401 || statusCode === 403) {
            console.log('User not authenticated - continuing in guest mode');
            if (typeof window !== 'undefined') {
              window.__USER_DATA__ = null;
            }
            if (isMounted) {
              setUser(null);
            }
          } else {
            // Re-throw other errors
            throw userError;
          }
        }

        // If guest, verify this portal allows guest checkout before continuing.
        // Redirect to login if the middleware returns 403.
        if (!authenticatedUser) {
          try {
            await fetchWithErrorHandling(`/api/v2/guest/client`);
          } catch (guestError: unknown) {
            if (guestError instanceof ApiError && guestError.statusCode === 403) {
              window.location.href = '/login';
              return;
            }
          }
        }

        // Fetch promo codes (only for authenticated users)
        if (authenticatedUser) {
          try {
            const promoResponse = await PromoService.getUserPromos();
            // Transform array to Record<string, PromoCode> keyed by promo field
            const promoRecord: Record<string, PromoCode> = {};
            promoResponse.data.forEach((promo) => {
              if (promo.promo) {
                promoRecord[promo.promo] = promo;
              }
            });
            if (typeof window !== 'undefined') {
              window.__USER_PROMO_CODES__ = promoRecord;
            }
            if (isMounted) {
              setPromoCodes(promoRecord);
            }
          } catch (promoError) {
            console.warn('Failed to fetch promo codes, using empty promos:', promoError);
            // Keep empty object if promo fetch fails - use same reference
            const emptyPromos = {};
            if (typeof window !== 'undefined') {
              window.__USER_PROMO_CODES__ = emptyPromos;
            }
            if (isMounted) {
              setPromoCodes(emptyPromos);
            }
          }
        }

        // Mark as fetched
        if (typeof window !== 'undefined') {
          window.__USER_FETCHED__ = true;
          window.__USER_FETCH_IN_PROGRESS__ = false;
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load user data';
        if (typeof window !== 'undefined') {
          window.__USER_FETCH_IN_PROGRESS__ = false;
        }
        if (isMounted) {
          setError(errorMessage);
        }
        console.error('Error fetching user data:', err);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchUserData();

    return () => {
      isMounted = false;
    };
  }, []);

  return {
    user,
    promoCodes,
    loading,
    error,
  };
}
